
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { 
  Search, 
  SlidersHorizontal, 
  X, 
  Filter, 
  Star, 
  Crown,
  Sparkles,
  ChevronDown,
  ChevronUp
} from "lucide-react";
import { Category } from "@/types/common";

interface LuxuryFilterSidebarProps {
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  selectedCategory: string;
  setSelectedCategory: (category: string) => void;
  sortBy: string;
  setSortBy: (sort: string) => void;
  categories?: Category[];
  className?: string;
}

const LuxuryFilterSidebar = ({
  searchTerm,
  setSearchTerm,
  selectedCategory,
  setSelectedCategory,
  sortBy,
  setSortBy,
  categories,
  className = ""
}: LuxuryFilterSidebarProps) => {
  const [priceRange, setPriceRange] = useState([0, 2000]);
  const [expandedSections, setExpandedSections] = useState({
    categories: true,
    price: true,
    features: true,
    rating: true
  });

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const sortOptions = [
    { value: "name", label: "Name A-Z" },
    { value: "price_low", label: "Price: Low to High" },
    { value: "price_high", label: "Price: High to Low" },
    { value: "featured", label: "Featured First" },
    { value: "newest", label: "Newest First" },
  ];

  const premiumFeatures = [
    { id: "premium", label: "Premium Quality", icon: Crown },
    { id: "limited", label: "Limited Edition", icon: Sparkles },
    { id: "exclusive", label: "Exclusive", icon: Star },
  ];

  return (
    <motion.div
      className={`bg-white/80 backdrop-blur-xl border-0 rounded-2xl shadow-2xl p-6 ${className}`}
      initial={{ opacity: 0, x: -50 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.6 }}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gradient-to-r from-purple-500 to-blue-500 rounded-xl">
            <SlidersHorizontal className="h-5 w-5 text-white" />
          </div>
          <h3 className="text-xl font-semibold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
            Refine Selection
          </h3>
        </div>
      </div>

      {/* Premium Search */}
      <div className="mb-8">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <Input
            placeholder="Search luxury items..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-12 pr-4 py-3 bg-white/60 border-gray-200/50 rounded-xl focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500/50 transition-all duration-300"
          />
        </div>
      </div>

      {/* Categories Section */}
      <div className="mb-8">
        <motion.button
          onClick={() => toggleSection('categories')}
          className="flex items-center justify-between w-full mb-4 text-left"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <h4 className="text-lg font-medium text-gray-900">Collections</h4>
          {expandedSections.categories ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
        </motion.button>
        
        <AnimatePresence>
          {expandedSections.categories && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="space-y-2 overflow-hidden"
            >
              <motion.button
                onClick={() => setSelectedCategory("all")}
                className={`w-full text-left px-4 py-3 rounded-xl transition-all duration-300 ${
                  selectedCategory === "all"
                    ? "bg-gradient-to-r from-purple-500 to-blue-500 text-white shadow-lg"
                    : "bg-gray-50 hover:bg-gray-100 text-gray-700"
                }`}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <div className="flex items-center justify-between">
                  <span className="font-medium">All Collections</span>
                  <Sparkles className="h-4 w-4" />
                </div>
              </motion.button>
              
              {categories?.map((category) => (
                <motion.button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`w-full text-left px-4 py-3 rounded-xl transition-all duration-300 ${
                    selectedCategory === category.id
                      ? "bg-gradient-to-r from-purple-500 to-blue-500 text-white shadow-lg"
                      : "bg-gray-50 hover:bg-gray-100 text-gray-700"
                  }`}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {category.name}
                </motion.button>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Price Range Section */}
      <div className="mb-8">
        <motion.button
          onClick={() => toggleSection('price')}
          className="flex items-center justify-between w-full mb-4 text-left"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <h4 className="text-lg font-medium text-gray-900">Price Range</h4>
          {expandedSections.price ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
        </motion.button>
        
        <AnimatePresence>
          {expandedSections.price && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="space-y-4 overflow-hidden"
            >
              <div className="px-2">
                <Slider
                  value={priceRange}
                  onValueChange={setPriceRange}
                  max={2000}
                  min={0}
                  step={50}
                  className="w-full"
                />
              </div>
              <div className="flex items-center justify-between text-sm font-medium text-gray-600">
                <span>${priceRange[0]}</span>
                <span>${priceRange[1]}</span>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Premium Features */}
      <div className="mb-8">
        <motion.button
          onClick={() => toggleSection('features')}
          className="flex items-center justify-between w-full mb-4 text-left"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <h4 className="text-lg font-medium text-gray-900">Premium Features</h4>
          {expandedSections.features ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
        </motion.button>
        
        <AnimatePresence>
          {expandedSections.features && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="space-y-3 overflow-hidden"
            >
              {premiumFeatures.map((feature) => (
                <motion.label
                  key={feature.id}
                  className="flex items-center gap-3 cursor-pointer group"
                  whileHover={{ scale: 1.02 }}
                >
                  <input
                    type="checkbox"
                    className="w-5 h-5 rounded border-2 border-gray-300 text-purple-500 focus:ring-purple-500/20"
                  />
                  <feature.icon className="h-4 w-4 text-purple-500 group-hover:text-purple-600 transition-colors" />
                  <span className="text-gray-700 group-hover:text-gray-900 transition-colors">
                    {feature.label}
                  </span>
                </motion.label>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Sort Options */}
      <div className="mb-6">
        <h4 className="text-lg font-medium text-gray-900 mb-4">Sort By</h4>
        <div className="space-y-2">
          {sortOptions.map((option) => (
            <motion.button
              key={option.value}
              onClick={() => setSortBy(option.value)}
              className={`w-full text-left px-4 py-3 rounded-xl transition-all duration-300 ${
                sortBy === option.value
                  ? "bg-gradient-to-r from-purple-500 to-blue-500 text-white shadow-lg"
                  : "bg-gray-50 hover:bg-gray-100 text-gray-700"
              }`}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {option.label}
            </motion.button>
          ))}
        </div>
      </div>

      {/* Clear Filters */}
      <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
        <Button
          variant="outline"
          className="w-full border-2 border-gray-200 hover:border-purple-500 hover:text-purple-600 transition-all duration-300"
          onClick={() => {
            setSearchTerm("");
            setSelectedCategory("all");
            setSortBy("name");
            setPriceRange([0, 2000]);
          }}
        >
          <X className="h-4 w-4 mr-2" />
          Clear All Filters
        </Button>
      </motion.div>
    </motion.div>
  );
};

export default LuxuryFilterSidebar;
